"use client";

import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import Image from "next/image";
import React from "react";

interface PhotoGuideModalProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const PhotoGuideModal = ({ isModalOpen, setIsModalOpen }: PhotoGuideModalProps) => {
  return (
    <Transition appear show={isModalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[99999]"
        onClose={() => setIsModalOpen(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full overflow-hidden items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-2xl transform text-left align-middle shadow-xl transition-all rounded-2xl bg-white p-6 md:p-8">
                <div className="flex justify-between items-start mb-6">
                  <Dialog.Title as="h2" className="text-2xl font-bold text-gray-900 pt-4">
                    📸 How to Get Best Results
                  </Dialog.Title>
                  <button
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setIsModalOpen(false)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-6 h-6"
                    >
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>

                <section className="space-y-6">
                  {/* Step-by-step guide with example image under each step */}
                  <div className="space-y-6">
                    <p className="text-base text-gray-700 leading-relaxed mb-8">For best AI results, upload a recent photo (<strong>or multiple photos for even better resemblance</strong>) where you clearly look like yourself. Or create a new one by following these simple steps:</p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                          1
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">Find natural light</h3>
                          <p className="text-sm text-gray-600">Stand near a window and in front of a plain wall</p>
                        </div>
                      </div>
                      <div className="relative aspect-[3/2] rounded-lg overflow-hidden border-2 border-gray-200 ml-11">
                        <Image
                          src="/assets/images/1_guide.webp"
                          alt="Step 1 example"
                          fill
                          className="object-cover object-top"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                          2
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">Position your phone correctly</h3>
                          <p className="text-sm text-gray-600">Hold your phone up on the same line with your eyes</p>
                        </div>
                      </div>
                      <div className="relative aspect-[3/2] rounded-lg overflow-hidden border-2 border-gray-200 ml-11">
                        <Image
                          src="/assets/images/2_guide.webp"
                          alt="Step 2 example"
                          fill
                          className="object-cover object-top"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                          3
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">Take a natural selfie</h3>
                          <p className="text-sm text-gray-600">Relax and take a natural-looking photo</p>
                        </div>
                      </div>
                      <div className="relative aspect-[3/2] rounded-lg overflow-hidden border-2 border-gray-200 ml-11">
                        <Image
                          src="/assets/images/3_guide.webp"
                          alt="Step 3 example"
                          fill
                          className="object-cover object-top"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Important notes */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-900 mb-2 text-sm">⚠️ Important - Avoid these:</h4>
                    <ul className="space-y-1 text-sm text-amber-800">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>blurred photos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>heavily edited photos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>photos where face is covered or not completely visible</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>group photos</span>
                      </li>
                    </ul>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="w-full py-3 bg-primary text-white rounded-lg font-semibold hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Got it!
                  </button>
                </section>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PhotoGuideModal;

